import * as React from "react";

/** Aligns with Tailwind `md` (768px). Mobile shell applies below this width. */
export const MOBILE_BREAKPOINT = 768;

const MOBILE_MEDIA_QUERY = `(max-width: ${MOBILE_BREAKPOINT - 1}px)`;

/** Sync check for click handlers / URL coercion (avoids waiting on useIsMobile hydration). */
export function isMobileViewport(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  return window.matchMedia(MOBILE_MEDIA_QUERY).matches;
}

export function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = React.useState<boolean | undefined>(
    undefined,
  );

  React.useEffect(() => {
    const mql = window.matchMedia(MOBILE_MEDIA_QUERY);
    const onChange = (): void => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };
    mql.addEventListener("change", onChange);
    setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    return (): void => {
      mql.removeEventListener("change", onChange);
    };
  }, []);

  return !!isMobile;
}

/**
 * Approximate on-screen keyboard height (px) via Visual Viewport API.
 * Used to keep the chat composer above the keyboard on mobile.
 */
export function useKeyboardInset(): number {
  const [inset, setInset] = React.useState(0);

  React.useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) {
      return;
    }

    const update = (): void => {
      const raw = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      // Ignore small chrome / URL-bar jitter.
      const next = raw > 80 ? raw : 0;
      setInset((prev) => (prev === next ? prev : next));
    };

    update();
    vv.addEventListener("resize", update);
    return (): void => {
      vv.removeEventListener("resize", update);
    };
  }, []);

  return inset;
}
